"use client";
import React, { useEffect, useMemo, useState } from "react";
import { FaAward, FaDownload, FaEye, FaSearch, FaShieldAlt, FaTimesCircle } from "react-icons/fa";
import ManagementHeader from "../../../components/ManagementHeader";
import CertificatePreview from "../../../components/CertificatePreview";
import { supabase } from "../../../supabaseClient";
import { formatProfileName } from "../../../utils/profileName";
import { CERTIFICATE_TITLE_BY_TYPE, CERTIFICATE_TYPES, formatCertificateDate, openCertificatePdf } from "../../../lib/certificates";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = (awarderId = "") => ({ staffId: "", awarderId, certificateType: "Recognition", certificateTitle: CERTIFICATE_TITLE_BY_TYPE.Recognition, citation: "", awardedOn: today() });

export default function ManagementCertificates({ session, profile }) {
  const { isEnabled } = useFeatureFlags();
  const [profiles, setProfiles] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [form, setForm] = useState(() => emptyForm(session?.user?.id || ""));
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    const [profilesResult, certificatesResult] = await Promise.all([
      supabase.from("profiles").select("id,full_name,name_prefix,job_role,position,suspended").in("position", ["Staff", "Management"]).eq("suspended", false).order("full_name"),
      supabase.from("staff_certificates").select("*,recipient:profiles!staff_certificates_staff_id_fkey(id,full_name,name_prefix,job_role,position),awarder:profiles!staff_certificates_awarded_by_fkey(id,full_name,name_prefix,job_role,position),issuer:profiles!staff_certificates_issued_by_fkey(id,full_name,name_prefix),revoker:profiles!staff_certificates_revoked_by_fkey(id,full_name,name_prefix)").order("issued_at", { ascending: false }),
    ]);
    if (profilesResult.error) setMessage(profilesResult.error.message);
    if (certificatesResult.error) setMessage(certificatesResult.error.message);
    setProfiles(profilesResult.data || []);
    setCertificates(certificatesResult.data || []);
    setForm((current) => ({ ...current, awarderId: current.awarderId || session?.user?.id || "" }));
    setLoading(false);
  };

  useEffect(() => { void load(); }, []);

  const staffOptions = useMemo(() => profiles.filter((item) => ["Staff", "Management"].includes(item.position)), [profiles]);
  const managementOptions = useMemo(() => profiles.filter((item) => item.position === "Management"), [profiles]);
  const selectedStaff = staffOptions.find((item) => item.id === form.staffId);
  const selectedAwarder = managementOptions.find((item) => item.id === form.awarderId);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return certificates.filter((certificate) => {
      if (statusFilter !== "all" && certificate.status !== statusFilter) return false;
      if (!q) return true;
      return [certificate.certificate_number, certificate.certificate_title, certificate.certificate_type, certificate.citation, (certificate.recipient_name || formatProfileName(certificate.recipient, "")), (certificate.awarder_name || formatProfileName(certificate.awarder, ""))]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [certificates, search, statusFilter]);

  const setType = (certificateType) => setForm((current) => ({
    ...current,
    certificateType,
    certificateTitle: current.certificateTitle === CERTIFICATE_TITLE_BY_TYPE[current.certificateType] || !current.certificateTitle.trim()
      ? CERTIFICATE_TITLE_BY_TYPE[certificateType]
      : current.certificateTitle,
  }));

  const issue = async (event) => {
    event.preventDefault();
    if (!isEnabled("feature.certificate_issue")) return setMessage("Certificate issuing is currently disabled.");
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch("/api/certificates", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token || ""}` },
        body: JSON.stringify({ action: "issue", ...form }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || "Unable to issue certificate.");
      setMessage(`Certificate ${result.certificate?.certificate_number || ""} issued and stored as a PDF.`);
      setForm(emptyForm(session?.user?.id || ""));
      await load();
    } catch (error) {
      setMessage(error.message || "Unable to issue certificate.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (certificate) => {
    if (!isEnabled("feature.certificate_revoke")) return setMessage("Certificate revocation is currently disabled.");
    const reason = window.prompt(`Reason for revoking ${certificate.certificate_number}:`);
    if (!reason?.trim()) return;
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch("/api/certificates", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token || ""}` },
        body: JSON.stringify({ action: "revoke", certificateId: certificate.id, reason: reason.trim() }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || "Unable to revoke certificate.");
      setMessage("Certificate revoked. The original PDF has been retained for the audit record.");
      await load();
    } catch (error) {
      setMessage(error.message || "Unable to revoke certificate.");
    } finally {
      setBusy(false);
    }
  };

  const pdfAction = async (certificate, download = false) => {
    setMessage("");
    try { await openCertificatePdf({ certificate, session, download }); }
    catch (error) { setMessage(error.message); }
  };

  return <>
    <ManagementHeader session={session} profile={profile} />
    <main className="nhsuk-main-wrapper ruht-cert-admin" id="main-content" tabIndex="-1">
      <div className="nhsuk-width-container">
        <header className="ruht-cert-page-hero">
          <div><span className="pguh-eyebrow">Management</span><h1><FaAward /> Staff certificates</h1><p>Issue professional recognition certificates, store the official PDF and maintain the certificate register.</p></div>
          <div className="ruht-cert-page-hero__seal"><FaShieldAlt /><span>Permanent PDF<br/>record</span></div>
        </header>

        {message && <div className="nhsuk-inset-text" role="status">{message}</div>}

        <div className="ruht-cert-admin-layout">
          <section className="ruht-cert-panel ruht-cert-issue-panel">
            <div className="ruht-cert-panel__heading"><div><span className="pguh-eyebrow">New award</span><h2>Issue a certificate</h2></div></div>
            <form className="ruht-cert-form" onSubmit={issue}>
              <div className="ruht-cert-form-grid">
                <label><span>Staff member</span><select className="nhsuk-select" required value={form.staffId} onChange={(e) => setForm((current) => ({ ...current, staffId: e.target.value }))}><option value="">Choose staff member</option>{staffOptions.map((item) => <option key={item.id} value={item.id}>{formatProfileName(item)}{item.job_role ? ` - ${item.job_role}` : ""}</option>)}</select></label>
                <label><span>Awarded by</span><select className="nhsuk-select" required value={form.awarderId} onChange={(e) => setForm((current) => ({ ...current, awarderId: e.target.value }))}><option value="">Choose Management member</option>{managementOptions.map((item) => <option key={item.id} value={item.id}>{formatProfileName(item)}{item.job_role ? ` - ${item.job_role}` : ""}</option>)}</select></label>
              </div>
              <div className="ruht-cert-form-grid ruht-cert-form-grid--three">
                <label><span>Certificate type</span><select className="nhsuk-select" value={form.certificateType} onChange={(e) => setType(e.target.value)}>{CERTIFICATE_TYPES.map((type) => <option key={type}>{type}</option>)}</select></label>
                <label className="ruht-cert-field--wide"><span>Certificate title</span><input className="nhsuk-input" maxLength="120" required value={form.certificateTitle} onChange={(e) => setForm((current) => ({ ...current, certificateTitle: e.target.value }))} /></label>
                <label><span>Award date</span><input className="nhsuk-input" type="date" required value={form.awardedOn} onChange={(e) => setForm((current) => ({ ...current, awardedOn: e.target.value }))} /></label>
              </div>
              <label className="ruht-cert-citation"><span>Citation / reason for award</span><small>This wording is printed on the official certificate.</small><textarea className="nhsuk-textarea" rows="5" maxLength="500" required value={form.citation} onChange={(e) => setForm((current) => ({ ...current, citation: e.target.value }))} placeholder="In recognition of outstanding contribution, professionalism and commitment..."/><em>{form.citation.length}/500</em></label>
              <div className="ruht-cert-form__actions"><button type="submit" className="nhsuk-button" disabled={busy || !isEnabled("feature.certificate_issue")}><FaAward /> {busy ? "Issuing certificate..." : "Issue certificate & create PDF"}</button><small>Issued certificates cannot be silently edited. Revoke and reissue if a correction is required.</small></div>
            </form>
          </section>

          <section className="ruht-cert-panel ruht-cert-preview-panel">
            <div className="ruht-cert-panel__heading"><div><span className="pguh-eyebrow">Live preview</span><h2>Certificate appearance</h2></div></div>
            <CertificatePreview title={form.certificateTitle} type={form.certificateType} recipientName={formatProfileName(selectedStaff, "Staff member")} recipientRole={selectedStaff?.job_role || selectedStaff?.position} citation={form.citation} awarderName={formatProfileName(selectedAwarder, "RUHT Management")} awarderRole={selectedAwarder?.job_role || "Management"} awardedOn={form.awardedOn} />
            <p className="ruht-cert-preview-note">The stored PDF uses the same RUHT layout in print-ready landscape A4 format.</p>
          </section>
        </div>

        <section className="ruht-cert-panel ruht-cert-register">
          <div className="ruht-cert-register__heading"><div><span className="pguh-eyebrow">Certificate register</span><h2>Issued certificates</h2><p>{certificates.length} certificate record{certificates.length === 1 ? "" : "s"}.</p></div><div className="ruht-cert-register__filters"><label><FaSearch /><input className="nhsuk-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search certificates or staff" /></label><select className="nhsuk-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="all">All statuses</option><option value="active">Active</option><option value="revoked">Revoked</option></select></div></div>
          <div className="ruht-cert-list">
            {loading && <div className="ruht-cert-empty">Loading certificate register...</div>}
            {!loading && filtered.map((certificate) => <article key={certificate.id} className={`ruht-cert-row${certificate.status === "revoked" ? " is-revoked" : ""}`}>
              <div className="ruht-cert-row__icon"><FaAward /></div>
              <div className="ruht-cert-row__main"><div className="ruht-cert-row__top"><strong>{certificate.certificate_title}</strong><span className={`ruht-cert-status ruht-cert-status--${certificate.status}`}>{certificate.status}</span></div><h3>{certificate.recipient_name || formatProfileName(certificate.recipient)}</h3><p>{certificate.citation}</p><small>{certificate.certificate_number} · {formatCertificateDate(certificate.awarded_on)} · Awarded by {certificate.awarder_name || formatProfileName(certificate.awarder, "RUHT Management")}</small>{certificate.status === "revoked" && <div className="ruht-cert-revoked-note">Revoked{certificate.revoked_at ? ` ${new Date(certificate.revoked_at).toLocaleDateString("en-GB")}` : ""}: {certificate.revocation_reason || "No reason recorded"}</div>}</div>
              <div className="ruht-cert-row__actions"><button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={() => pdfAction(certificate, false)}><FaEye /> Open / print</button><button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={() => pdfAction(certificate, true)}><FaDownload /> Download PDF</button>{certificate.status === "active" && <button type="button" className="ruht-cert-revoke" onClick={() => revoke(certificate)} disabled={busy || !isEnabled("feature.certificate_revoke")}><FaTimesCircle /> Revoke</button>}</div>
            </article>)}
            {!loading && !filtered.length && <div className="ruht-cert-empty"><FaAward /><strong>No certificates found</strong><p>Issue a certificate or change the register filters.</p></div>}
          </div>
        </section>
      </div>
    </main>
  </>;
}
